import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import useGet from '@/core/composables/api/useGet'
import usePut from '@/core/composables/api/usePut'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { lessonStatusCode } from './lesson-status-code'
import type { GiveTeacherAssessmentBody, TeacherAssessment } from '../models/teacher-assessment'

export const useTeacherAssessment = () => {
  const route = useRoute()
  const lessonId = computed(() => route.params.lessonId as string)
  const endpoint = computed(() => `lesson/${lessonId.value}/teacher-assessment`)

  const { data, status, load, reload } = useGet<TeacherAssessment>(endpoint)
  const {
    mutate,
    status: saveStatus,
    error: saveError,
  } = usePut<void, GiveTeacherAssessmentBody>(endpoint)

  let saveQueue: Promise<unknown> = Promise.resolve()

  watch(
    lessonId,
    () => {
      load()
    },
    { immediate: true },
  )

  const saveAnswer = (
    memberId: string,
    indikatorId: string,
    value: number | null,
  ): Promise<boolean> => {
    const run = async (): Promise<boolean> => {
      if (data.value?.endedAt) {
        return false
      }
      await mutate({
        assessments: [{ memberId, answers: [{ indikatorId, value }] }],
      })
      if (saveStatus.value === ApiRequestStatus.Success) {
        return true
      }
      if (lessonStatusCode(saveError.value) === 409) {
        await reload()
      }
      return false
    }

    const result = saveQueue.then(run, run)
    saveQueue = result.then(
      () => undefined,
      () => undefined,
    )
    return result
  }

  return { assessment: data, status, lessonId, saveAnswer }
}
